/**
 * Budget vs actual, grouped by phase, category or month, with expandable rows.
 *
 * Costs and revenue are never added together: a row carries a Costs group, an Income group and a
 * Net (income less costs), and the groups that hold nothing on this project are not rendered at
 * all. One table serves all three groupings because it is the same data read three ways.
 */
import React, { useState } from 'react';
import { CaretRight } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
import { phaseMeta, UNASSIGNED_PHASE } from '../../../lib/projectPhases';
import {
  money, percent, usedPercent, varianceOf, varianceClass, monthLabel,
} from './financeFormat';

const EMPTY = { budget: 0, actual: 0 };

const Amount = ({ value, muted, strong }) =>
  value === null || value === undefined ? (
    <span className="text-foreground-muted/50">·</span>
  ) : (
    <span className={cn('tabular-nums', strong && 'font-medium', muted && 'text-foreground-muted')}>
      {money(value)}
    </span>
  );

const Variance = ({ budget, actual, income }) => {
  if (!budget && !actual) return <span className="text-foreground-muted/50">·</span>;
  const variance = varianceOf(budget, actual, income);
  return (
    <span className={cn('tabular-nums', varianceClass(variance))}>
      {variance.diff === 0 ? '—' : `${variance.favourable ? '+' : '−'}${money(Math.abs(variance.diff))}`}
    </span>
  );
};

const UtilisationBar = ({ actual, budget }) => {
  const used = usedPercent(actual, budget);
  if (used === null) return null;
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="h-1.5 w-16 bg-black/[0.07] rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full', used > 100 ? 'bg-red-500' : 'bg-primary')}
          style={{ width: `${Math.min(100, Math.abs(used))}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-foreground-muted w-11 text-right">{percent(used, 0)}</span>
    </div>
  );
};

/** The numeric cells of one row, in the order the header declares them. */
const Cells = ({ row, groups, child }) => {
  const costs = row.costs || EMPTY;
  const income = row.income || EMPTY;
  const net = row.net || { budget: income.budget - costs.budget, actual: income.actual - costs.actual };
  const pad = child ? 'py-2' : 'py-2.5';
  const blank = <span className="text-foreground-muted/50" aria-label="not applicable" title="Nothing on this side of the ledger">·</span>;
  // A row with nothing on one side of the ledger shows nothing there - not a zero
  const hasCosts = !!(costs.budget || costs.actual);
  const hasIncome = !!(income.budget || income.actual);

  return (
    <>
      {groups.costs && (
        <>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasCosts ? <Amount value={costs.budget} muted /> : blank}
          </td>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasCosts ? <Amount value={costs.actual} strong={!child} /> : blank}
          </td>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasCosts ? <Variance budget={costs.budget} actual={costs.actual} /> : blank}
          </td>
        </>
      )}
      {groups.income && (
        <>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasIncome ? <Amount value={income.budget} muted /> : blank}
          </td>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasIncome ? <Amount value={income.actual} strong={!child} /> : blank}
          </td>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasIncome ? <Variance budget={income.budget} actual={income.actual} income /> : blank}
          </td>
        </>
      )}
      {groups.net && (
        <>
          {/* Net only says something when both sides are present - otherwise it just negates a cost */}
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasCosts && hasIncome ? <Amount value={net.budget} muted /> : blank}
          </td>
          <td className={cn(pad, 'px-3 text-right text-sm')}>
            {hasCosts && hasIncome ? <Amount value={net.actual} strong={!child} /> : blank}
          </td>
        </>
      )}
      <td className={cn(pad, 'pr-4 pl-3')}>
        {!child && <UtilisationBar actual={costs.actual} budget={costs.budget} />}
      </td>
    </>
  );
};

const Row = ({ row, groups, onDrill, expandable }) => {
  const [open, setOpen] = useState(false);
  const children = row.children || [];

  return (
    <>
      <tr
        className={cn('border-b border-black/[0.05] hover:bg-black/[0.015] transition-colors', expandable && 'cursor-pointer')}
        onClick={() => expandable && children.length > 0 && setOpen((v) => !v)}
        data-testid={`bva-row-${row.key}`}
      >
        <td className="py-2.5 pl-4 pr-3">
          <div className="flex items-center gap-2">
            {expandable && children.length > 0 ? (
              <CaretRight className={cn('h-3.5 w-3.5 text-foreground-muted transition-transform', open && 'rotate-90')} />
            ) : (
              <span className="w-3.5" />
            )}
            <span className="text-sm font-medium">{row.label}</span>
            {row.badge && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/[0.05] text-foreground-muted">
                {row.badge}
              </span>
            )}
          </div>
        </td>
        <Cells row={row} groups={groups} />
      </tr>

      {open &&
        children.map((child) => (
          <tr
            key={child.key}
            className={cn(
              'border-b border-black/[0.04] bg-black/[0.012]',
              onDrill && 'cursor-pointer hover:bg-black/[0.03]'
            )}
            onClick={() => onDrill && onDrill(row, child)}
            data-testid={`bva-child-${child.key}`}
          >
            <td className="py-2 pl-11 pr-3 text-sm text-foreground-muted">
              {child.label}
              {child.meta && <span className="ml-2 text-xs">{child.meta}</span>}
            </td>
            <Cells row={child} groups={groups} child />
          </tr>
        ))}
    </>
  );
};

/** An account line belongs to exactly one side, so it fills that side's group only. */
const accountRow = (line, key, meta) => {
  const kind = line.kind || (line.account_type === 'revenue' ? 'income' : 'cost');
  const side = { budget: line.budget, actual: line.actual };
  return {
    key,
    label: line.account_name || line.account_code,
    meta,
    kind,
    costs: kind === 'cost' ? side : EMPTY,
    income: kind === 'income' ? side : EMPTY,
    phase: line.phase,
    category: line.category,
  };
};

/** Turn the report into rows for the chosen grouping. */
export const buildRows = ({ groupBy, report, months, projectType }) => {
  if (groupBy === 'phase') {
    return (report?.by_phase || []).map((phase) => {
      const meta = phase.phase === UNASSIGNED_PHASE.value ? UNASSIGNED_PHASE : phaseMeta(phase.phase);
      return {
        key: phase.phase,
        label: phase.phase_label || meta.label,
        badge: `${phase.entries_count || 0} entries`,
        costs: phase.costs,
        income: phase.income,
        net: phase.net,
        children: (phase.categories || []).flatMap((cat) =>
          (cat.accounts || []).map((acc) =>
            accountRow(acc, `${phase.phase}-${acc.account_code}`, cat.category)
          )
        ),
      };
    });
  }

  if (groupBy === 'month') {
    return (months || []).map((m) => ({
      key: m.month,
      label: monthLabel(m.month),
      badge: `${m.entries_count || 0} entries`,
      costs: m.costs,
      income: m.income,
      net: m.net,
      month: m.month,
      children: [],
    }));
  }

  // Category
  const accountsByCategory = {};
  (report?.by_account || []).forEach((line) => {
    const key = line.category || 'Uncategorized';
    (accountsByCategory[key] = accountsByCategory[key] || []).push(line);
  });

  return (report?.by_category || []).map((cat) => ({
    key: cat.category,
    label: cat.category,
    badge: `${cat.entries_count || 0} entries`,
    costs: cat.costs,
    income: cat.income,
    net: cat.net,
    category: cat.category,
    children: (accountsByCategory[cat.category] || []).map((line) =>
      accountRow(line, `${cat.category}-${line.account_code}-${line.phase || 'none'}`, line.phase_label)
    ),
  }));
};

const sumGroup = (rows, group, field) =>
  rows.reduce((total, row) => total + Number(row[group]?.[field] || 0), 0);

export const BudgetVsActualTable = ({ rows, currency = 'EUR', onDrill, expandable = true, emptyLabel }) => {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-foreground-muted px-4 py-8 text-center">
        {emptyLabel || 'Nothing recorded yet.'}
      </p>
    );
  }

  const totals = {
    costs: { budget: sumGroup(rows, 'costs', 'budget'), actual: sumGroup(rows, 'costs', 'actual') },
    income: { budget: sumGroup(rows, 'income', 'budget'), actual: sumGroup(rows, 'income', 'actual') },
  };
  // Only render the side of the ledger this project actually has
  const groups = {
    costs: !!(totals.costs.budget || totals.costs.actual),
    income: !!(totals.income.budget || totals.income.actual),
  };
  groups.net = groups.costs && groups.income;
  if (!groups.costs && !groups.income) groups.costs = true;

  const totalRow = {
    costs: totals.costs,
    income: totals.income,
    net: {
      budget: totals.income.budget - totals.costs.budget,
      actual: totals.income.actual - totals.costs.actual,
    },
  };

  const head = (label, key, className) => (
    <th
      key={key}
      className={cn(
        'py-2 px-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted whitespace-nowrap',
        className
      )}
    >
      {label}
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px]" data-testid="budget-vs-actual-table">
        <thead>
          <tr className="border-b border-black/[0.05]">
            <th className="py-1.5 pl-4 pr-3" />
            {groups.costs && (
              <th colSpan={3} className="py-1.5 px-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">
                Costs
              </th>
            )}
            {groups.income && (
              <th colSpan={3} className="py-1.5 px-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                Revenue
              </th>
            )}
            {groups.net && (
              <th colSpan={2} className="py-1.5 px-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">
                Net
              </th>
            )}
            <th className="py-1.5 pr-4 pl-3" />
          </tr>
          <tr className="border-b border-black/[0.08]">
            <th className="py-2 pl-4 pr-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
              Line
            </th>
            {groups.costs && [
              head('Budget', 'cb'),
              head('Actual', 'ca'),
              head('Variance', 'cv'),
            ]}
            {groups.income && [
              head('Budget', 'ib'),
              head('Actual', 'ia'),
              head('Variance', 'iv'),
            ]}
            {groups.net && [head('Plan', 'nb'), head('Actual', 'na')]}
            {head('Cost used', 'used', 'pr-4 pl-3')}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Row key={row.key} row={row} groups={groups} onDrill={onDrill} expandable={expandable} />
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-black/[0.03] font-semibold" data-testid="bva-totals">
            <td className="py-3 pl-4 pr-3 text-sm">Total</td>
            <Cells row={totalRow} groups={groups} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default BudgetVsActualTable;
